import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { shippixService } from '../../services/shippixService';
import { shippingAddressService } from '../../services/shippingAddressService';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { 
  Button, 
  Card, 
  CardContent, 
  Grid, 
  TextField, 
  Typography, 
  IconButton, 
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Divider,
  CircularProgress,
  Tooltip,
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Switch,
  InputAdornment,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useToast } from '../../contexts/ToastContext';
import dayjs from 'dayjs';
import { 
  LocalPrintshop as LocalPrintshopIcon, 
  Receipt as ReceiptIcon, 
  Add as AddIcon, 
  ListAlt as ListAltIcon, 
  Calculate as CalculateIcon, 
  Delete as DeleteIcon 
} from '@mui/icons-material';
import AddressAutocomplete from '../../components/shipping/AddressAutocomplete';
import { ShippixCreateShipment, ShippixPriceCalcResponse, ShippixService, ShippixVolume, ShippingAddress } from '../../types/shippix';

const darkBlueColor = 'rgb(10,25,47)';

export default function CreateShipment() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { balance, refreshBalance } = useWallet();
  const { show } = useToast();
  const [loading, setLoading] = useState(false);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [priceResult, setPriceResult] = useState<ShippixPriceCalcResponse | null>(null);
  const [services, setServices] = useState<ShippixService[]>([]);
  const [volumes, setVolumes] = useState<ShippixVolume[]>([
    { weight: '', length: '', width: '', height: '' }
  ]);
  const [shipment, setShipment] = useState<ShippixCreateShipment>({
    service: '',
    sender_country: 'PT',
    recipient_country: 'PT',
    volumes: 1,
    weight: 1,
    date: dayjs().format('YYYY-MM-DD'),
    volume_items: [],
    charge_price: 0
  });
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdTracking, setCreatedTracking] = useState<string>('');
  const [isChargeEnabled, setIsChargeEnabled] = useState(false);
  const [openPriceDialog, setOpenPriceDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [shipmentPrice, setShipmentPrice] = useState<number>(0);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [volumeErrors, setVolumeErrors] = useState<{ [key: number]: string[] }>({});

  // Adicionar tipos de volume
  const volumeTypes = [
    { id: 'outro', name: 'Outro', length: '', width: '', height: '' },
    { id: 'a4', name: 'Saco A4', length: '32', width: '21', height: '' },
    { id: 'a3', name: 'Saco A3', length: '45', width: '45', height: '' },
    { id: 'europalete', name: 'Europalete', length: '120', width: '80', height: '' },
  ];

  // Estado para o tipo de volume selecionado
  const [selectedVolumeType, setSelectedVolumeType] = useState<string>('outro');

  const handleVolumeTypeChange = (index: number, type: string) => {
    const selectedType = volumeTypes.find(vt => vt.id === type);
    if (selectedType) {
      const updatedVolumes = [...volumes];
      updatedVolumes[index] = {
        ...updatedVolumes[index],
        length: selectedType.length,
        width: selectedType.width,
        height: selectedType.height
      };
      setVolumes(updatedVolumes);
    }
  };

  const validateVolume = (volume: ShippixVolume, index: number) => {
    const errors: string[] = [];

    // Verifica peso
    if (parseFloat(volume.weight) > 30) {
      errors.push('O peso não pode exceder 30kg');
    }

    // Verifica dimensões
    const sum = parseFloat(volume.length) + 
               parseFloat(volume.width) + 
               parseFloat(volume.height);
    if (sum > 250) {
      errors.push('A soma das medidas não pode exceder 250cm');
    }

    return errors;
  };

  const handleVolumeChange = (index: number, field: keyof ShippixVolume, value: string) => {
    const newVolumes = [...volumes];
    newVolumes[index] = {
      ...newVolumes[index],
      [field]: value
    };
    setVolumes(newVolumes);

    // Valida o volume alterado
    const newErrors = { ...volumeErrors };
    newVolumes.forEach((volume, idx) => {
      const errors = validateVolume(volume, idx);
      if (errors.length > 0) {
        newErrors[idx] = errors;
      } else {
        delete newErrors[idx];
      }
    });
    setVolumeErrors(newErrors);
  };

  const validateVolumes = () => {
    const newErrors: { [key: number]: string[] } = {};
    let hasErrors = false;

    volumes.forEach((volume, index) => {
      const errors = validateVolume(volume, index);
      if (errors.length > 0) {
        newErrors[index] = errors;
        hasErrors = true;
      }
    });

    setVolumeErrors(newErrors);
    
    if (hasErrors) {
      setErrorMessage('Por favor, corrija os erros nos volumes antes de prosseguir.\nPara mais informações fale com o apoio ao cliente pelo e-mail ola@lognow.pt');
      setErrorModalOpen(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    const loadServices = async () => {
      if (!currentUser?.companyId) return;
      
      try {
        const servicesList = await shippixService.listServices(currentUser.companyId);
        const uniqueServices = Array.from(new Map(servicesList.map(item => [item.code, item])).values());
        setServices(uniqueServices);
      } catch (error) {
        console.error('Erro ao carregar serviços:', error);
        show(error instanceof Error ? error.message : 'Erro ao carregar serviços', 'error');
      }
    };

    loadServices();
  }, [currentUser?.companyId, show]);

  // Atualizar volumes e peso total quando os volumes individuais mudarem
  useEffect(() => {
    const totalWeight = volumes.reduce((sum, volume) => sum + (parseFloat(volume.weight) || 0), 0);
    setShipment(prev => ({
      ...prev,
      volumes: volumes.length,
      weight: totalWeight,
      volume_items: volumes
    }));
  }, [volumes]);

  useEffect(() => {
    setVolumes([{ weight: '', length: '', width: '', height: '' }]);
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser?.companyId) return;

    try {
      // Primeiro calcula o preço
      const priceRequest = {
        service_code: shipment.service,
        sender_zip_code: shipment.sender_zip_code,
        sender_country: shipment.sender_country || 'PT',
        recipient_zip_code: shipment.recipient_zip_code,
        recipient_country: shipment.recipient_country || 'PT',
        volume_items: volumes.map(vol => ({
          length: Number(vol.length) || 0,
          width: Number(vol.width) || 0,
          height: Number(vol.height) || 0,
          weight: Number(vol.weight) || 0
        })),
        companyId: currentUser.companyId,
        charge_price: isChargeEnabled ? shipment.charge_price : undefined
      };

      const priceResult = await shippixService.calculatePrice(priceRequest);
      
      // Verifica se há saldo suficiente
      if (balance < priceResult.total) {
        show('Saldo insuficiente na wallet. Por favor, carregue a sua wallet.', 'error');
        return;
      }

      setShipmentPrice(priceResult.total);
      setOpenConfirmDialog(true);
    } catch (error) {
      console.error('Erro ao calcular preço:', error);
      show(error instanceof Error ? error.message : 'Erro ao calcular preço do envio', 'error');
    }
  };

  const handleConfirmShipment = async () => {
    if (!currentUser?.companyId || !currentUser?.uid) return;
    
    // Valida os volumes antes de prosseguir
    if (!validateVolumes()) {
      return;
    }

    setLoading(true);

    try {
      // 1. Verificar saldo na wallet
      const walletRef = doc(db, 'wallets', currentUser.uid);
      const walletDoc = await getDoc(walletRef);
      
      if (!walletDoc.exists()) {
        throw new Error('Wallet não encontrada');
      }

      const currentBalance = walletDoc.data().balance || 0;
      if (currentBalance < shipmentPrice) {
        throw new Error('Saldo insuficiente na wallet');
      }

      // 2. Tentar criar o envio primeiro
      let result;
      try {
        result = await shippixService.createShipment(currentUser.companyId, {
          ...shipment,
          walletDeduction: shipmentPrice
        });

        // Verifica se a resposta é um array (formato de erro da API)
        if (Array.isArray(result)) {
          const apiMessage = result[0]?.[0]?.message || 'Erro ao processar o envio';
          setErrorMessage(`${apiMessage}\nPara mais informações fale com o apoio ao cliente pelo e-mail ola@lognow.pt`);
          setErrorModalOpen(true);
          setLoading(false);
          return;
        }

        // Verifica se é uma resposta válida
        if (!result || !result.tracking_code) {
          const apiMessage = result?.message || 'Erro ao processar o envio';
          setErrorMessage(`${apiMessage}\nPara mais informações fale com o apoio ao cliente pelo e-mail ola@lognow.pt`);
          setErrorModalOpen(true);
          setLoading(false);
          return;
        }

      } catch (error: any) {
        console.error('Erro ao criar envio:', error);
        
        // Tenta pegar mensagem de erro da API em diferentes formatos
        let apiMessage = 'Erro ao processar o envio';
        
        if (error?.response?.data) {
          if (Array.isArray(error.response.data)) {
            apiMessage = error.response.data[0]?.[0]?.message || 
                        error.response.data[0]?.message ||
                        apiMessage;
          } else {
            apiMessage = error.response.data.message || apiMessage;
          }
        } else if (error?.message) {
          apiMessage = error.message;
        }
        
        setErrorMessage(`${apiMessage}\nPara mais informações fale com o apoio ao cliente pelo e-mail ola@lognow.pt`);
        setErrorModalOpen(true);
        setLoading(false);
        return;
      }

      // Só continua aqui se o envio foi criado com sucesso
      if (!result?.tracking_code) {
        throw new Error('Código de rastreio não gerado');
      }

      // 3. Atualizar o saldo na wallet
      await updateDoc(walletRef, {
        balance: currentBalance - shipmentPrice,
        updatedAt: new Date().toISOString()
      });

      // 4. Registrar a transação
      await addDoc(collection(db, 'walletTransactions'), {
        type: 'shipping_payment',
        amount: -shipmentPrice,
        description: `Pagamento de envio - Tracking: ${result.tracking_code}`,
        createdAt: new Date().toISOString(),
        status: 'completed',
        companyId: currentUser.companyId,
        userId: currentUser.uid,
        source: 'shipping',
        trackingCode: result.tracking_code
      });

      // 5. Atualizar o saldo no frontend
      refreshBalance();

      setCreatedTracking(result.tracking_code);
      setSuccessModalOpen(true);
      show('Envio criado com sucesso!', 'success');
      setOpenConfirmDialog(false);
    } catch (error) {
      console.error('Erro ao processar envio:', error);
      setErrorMessage(`${error instanceof Error ? error.message : 'Erro ao processar envio'}\nPara mais informações fale com o apoio ao cliente pelo e-mail ola@lognow.pt`);
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculatePrice = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      if (!currentUser?.companyId) {
        show('Utilizador sem empresa associada', 'error');
        return;
      }

      if (!shipment.service) {
        show('Por favor, selecione um serviço', 'warning');
        return;
      }

      if (!shipment.sender_zip_code || !shipment.recipient_zip_code) {
        show('Os códigos postais são obrigatórios', 'warning');
        return;
      }

      setCalculatingPrice(true);
      setOpenPriceDialog(true);

      const priceRequest = {
        service_code: shipment.service,
        sender_zip_code: shipment.sender_zip_code,
        sender_country: shipment.sender_country || 'PT',
        recipient_zip_code: shipment.recipient_zip_code,
        recipient_country: shipment.recipient_country || 'PT',
        volume_items: volumes.map(vol => ({
          length: Number(vol.length) || 0,
          width: Number(vol.width) || 0,
          height: Number(vol.height) || 0,
          weight: Number(vol.weight) || 0
        })),
        companyId: currentUser.companyId,
        charge_price: isChargeEnabled ? shipment.charge_price : undefined
      };

      const price = await shippixService.calculatePrice(priceRequest);
      setPriceResult(price);
    } catch (error) {
      show(error instanceof Error ? error.message : 'Erro ao calcular preço', 'error');
      setOpenPriceDialog(false);
    } finally {
      setCalculatingPrice(false);
    }
  };

  const handleClosePriceDialog = () => {
    setOpenPriceDialog(false);
    setPriceResult(null);
  };

  const handleSenderChange = (address: Partial<ShippingAddress>) => {
    console.log(' Alterando dados do remetente:', address);
    setShipment(prev => ({
      ...prev,
      sender_name: address.name || '',
      sender_address: address.address || '',
      sender_city: address.city || '',
      sender_zip_code: address.zip_code || '',
      sender_country: address.country || 'PT',
      sender_phone: address.phone || ''
    }));
  };

  const handleRecipientChange = (address: Partial<ShippingAddress>) => {
    console.log(' Alterando dados do destinatário:', address);
    setShipment(prev => ({
      ...prev,
      recipient_name: address.name || '',
      recipient_address: address.address || '',
      recipient_city: address.city || '',
      recipient_zip_code: address.zip_code || '',
      recipient_country: address.country || 'PT',
      recipient_phone: address.phone || ''
    }));
  };

  const handleSaveAddress = async (type: 'sender' | 'recipient') => {
    if (!currentUser?.uid) return;

    try {
      const data = type === 'sender' ? {
        type: 'sender' as const,
        name: shipment.sender_name || '',
        address: shipment.sender_address || '',
        city: shipment.sender_city || '',
        zip_code: shipment.sender_zip_code || '',
        country: shipment.sender_country,
        phone: shipment.sender_phone || ''
      } : {
        type: 'recipient' as const,
        name: shipment.recipient_name || '',
        address: shipment.recipient_address || '',
        city: shipment.recipient_city || '',
        zip_code: shipment.recipient_zip_code || '',
        country: shipment.recipient_country,
        phone: shipment.recipient_phone || ''
      };

      await shippingAddressService.saveAddress(currentUser.uid, data);
      show('Endereço salvo com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao salvar endereço:', error);
      show(error instanceof Error ? error.message : 'Erro ao salvar endereço', 'error');
    }
  };

  const base64ToBlob = (base64: string, type: string = 'application/pdf'): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type });
  };

  const handlePrintLabel = async () => {
    if (!currentUser?.companyId) {
      show('CompanyId é obrigatório para imprimir etiqueta', 'error');
      return;
    }
    try {
      const labelBase64 = await shippixService.getShipmentLabel(createdTracking, currentUser.companyId);
      const blob = base64ToBlob(labelBase64);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      show(error instanceof Error ? error.message : 'Erro ao imprimir etiqueta', 'error');
    }
  };

  const handlePrintGuide = async () => {
    if (!currentUser?.companyId) {
      show('CompanyId é obrigatório para imprimir guia', 'error');
      return;
    }
    try {
      const guideBase64 = await shippixService.getShipmentGuide(createdTracking, currentUser.companyId);
      const blob = base64ToBlob(guideBase64);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      show(error instanceof Error ? error.message : 'Erro ao imprimir guia', 'error');
    }
  };

  const handleCreateNewShipment = () => {
    setSuccessModalOpen(false);
    // Resetar o formulário
    setShipment({
      service: '',
      sender_zip_code: '',
      sender_country: 'PT',
      recipient_zip_code: '',
      recipient_country: 'PT'
    });
    setVolumes([{ weight: '', length: '', width: '', height: '' }]);
    setPriceResult(null);
  };

  const handleViewShipments = () => {
    setSuccessModalOpen(false);
    navigate('/dashboard/shipping');
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Criar Novo Envio
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Data e Serviço */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Data do Envio"
                value={dayjs(shipment.date)}
                onChange={(newValue) => {
                  setShipment(prev => ({
                    ...prev,
                    date: newValue ? newValue.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
                  }));
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Serviço"
                name="service"
                value={shipment.service}
                onChange={(e) => setShipment(prev => ({ ...prev, service: e.target.value }))}
                required
              >
                {services.map((service, index) => (
                  <MenuItem key={`${service.code}-${index}`} value={service.code}>
                    {service.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Remetente */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Dados do Remetente
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <AddressAutocomplete
                type="sender"
                userId={currentUser?.uid || ''}
                value={{
                  name: shipment.sender_name,
                  address: shipment.sender_address,
                  city: shipment.sender_city,
                  zip_code: shipment.sender_zip_code,
                  country: shipment.sender_country,
                  phone: shipment.sender_phone
                }}
                onChange={handleSenderChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Remetente"
                name="sender_name"
                value={shipment.sender_name || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, sender_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefone do Remetente"
                name="sender_phone"
                value={shipment.sender_phone || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, sender_phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Morada do Remetente"
                name="sender_address"
                value={shipment.sender_address || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, sender_address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Código Postal"
                name="sender_zip_code"
                value={shipment.sender_zip_code || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, sender_zip_code: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cidade"
                name="sender_city"
                value={shipment.sender_city || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, sender_city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="País"
                  name="sender_country"
                  value={shipment.sender_country}
                  onChange={(e) => setShipment(prev => ({ ...prev, sender_country: e.target.value }))}
                  required
                />
                <Button
                  variant="outlined"
                  onClick={() => handleSaveAddress('sender')}
                  disabled={!shipment.sender_name || !shipment.sender_address}
                  sx={{ bgcolor: darkBlueColor, '&:hover': { bgcolor: darkBlueColor }, color: 'white' }}
                >
                  Guardar
                </Button>
              </Box>
            </Grid>

            {/* Destinatário */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Dados do Destinatário
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <AddressAutocomplete
                type="recipient"
                userId={currentUser?.uid || ''}
                value={{
                  name: shipment.recipient_name,
                  address: shipment.recipient_address,
                  city: shipment.recipient_city,
                  zip_code: shipment.recipient_zip_code,
                  country: shipment.recipient_country,
                  phone: shipment.recipient_phone
                }}
                onChange={handleRecipientChange}
                required={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Destinatário"
                name="recipient_name"
                value={shipment.recipient_name || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, recipient_name: e.target.value }))}
                required={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefone do Destinatário"
                name="recipient_phone"
                value={shipment.recipient_phone || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, recipient_phone: e.target.value }))}
                required={false}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Morada do Destinatário"
                name="recipient_address"
                value={shipment.recipient_address || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, recipient_address: e.target.value }))}
                required={false}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Código Postal"
                name="recipient_zip_code"
                value={shipment.recipient_zip_code || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, recipient_zip_code: e.target.value }))}
                required={false}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cidade"
                name="recipient_city"
                value={shipment.recipient_city || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, recipient_city: e.target.value }))}
                required={false}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="País"
                  name="recipient_country"
                  value={shipment.recipient_country}
                  onChange={(e) => setShipment(prev => ({ ...prev, recipient_country: e.target.value }))}
                  required={false}
                />
                <Button
                  variant="outlined"
                  onClick={() => handleSaveAddress('recipient')}
                  disabled={!shipment.recipient_name || !shipment.recipient_address}
                  sx={{ bgcolor: darkBlueColor, '&:hover': { bgcolor: darkBlueColor }, color: 'white' }}
                >
                  Guardar
                </Button>
              </Box>
            </Grid>

            {/* Referência */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Referência"
                name="reference"
                value={shipment.reference || ''}
                onChange={(e) => setShipment(prev => ({ ...prev, reference: e.target.value }))}
              />
            </Grid>

            {/* Volumes */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: darkBlueColor }}>
                    Volumes ({volumes.length})
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setVolumes([...volumes, { weight: '', length: '', width: '', height: '' }])}
                    startIcon={<AddIcon />}
                    size="small"
                    sx={{ bgcolor: darkBlueColor, '&:hover': { bgcolor: darkBlueColor }, color: 'white' }}
                  >
                    Adicionar Volume
                  </Button>
                </Box>

                {volumes.map((volume, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 3,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: darkBlueColor }}>
                        Volume {index + 1}
                      </Typography>
                      {volumes.length > 1 && (
                        <IconButton 
                          onClick={() => setVolumes(volumes.filter((_, i) => i !== index))}
                          color="error"
                          size="small"
                          sx={{ 
                            '&:hover': { 
                              bgcolor: 'error.light',
                              '& .MuiSvgIcon-root': {
                                color: 'white'
                              }
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    {volumeErrors[index]?.length > 0 && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {volumeErrors[index].map((error, errorIndex) => (
                          <Typography key={errorIndex} variant="body2">
                            {error}
                          </Typography>
                        ))}
                      </Alert>
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`volume-type-label-${index}`}>Tipo de Volume</InputLabel>
                          <Select
                            labelId={`volume-type-label-${index}`}
                            value={selectedVolumeType}
                            onChange={(e) => {
                              setSelectedVolumeType(e.target.value);
                              handleVolumeTypeChange(index, e.target.value);
                            }}
                            label="Tipo de Volume"
                          >
                            {volumeTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                {type.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          fullWidth
                          label="Peso (kg)"
                          type="number"
                          value={volume.weight}
                          onChange={(e) => handleVolumeChange(index, 'weight', e.target.value)}
                          error={parseFloat(volume.weight) > 30}
                          helperText={parseFloat(volume.weight) > 30 ? "Máximo 30kg" : ""}
                          required
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">kg</InputAdornment>
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          fullWidth
                          label="Comprimento"
                          type="number"
                          value={volume.length}
                          onChange={(e) => handleVolumeChange(index, 'length', e.target.value)}
                          required
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">cm</InputAdornment>
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          fullWidth
                          label="Largura"
                          type="number"
                          value={volume.width}
                          onChange={(e) => handleVolumeChange(index, 'width', e.target.value)}
                          required
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">cm</InputAdornment>
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          fullWidth
                          label="Altura"
                          type="number"
                          value={volume.height}
                          onChange={(e) => handleVolumeChange(index, 'height', e.target.value)}
                          required
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">cm</InputAdornment>
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Paper>
            </Grid>

            {/* Campo de Cobrança */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: darkBlueColor }}>
                    Cobrança
                  </Typography>
                  <Switch
                    checked={isChargeEnabled}
                    onChange={(e) => setIsChargeEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: darkBlueColor,
                        '&:hover': { bgcolor: `${darkBlueColor}10` }
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: darkBlueColor
                      }
                    }}
                  />
                </Box>
                
                {isChargeEnabled && (
                  <TextField
                    fullWidth
                    label="Valor a Cobrar"
                    type="number"
                    value={shipment.charge_price || ''}
                    onChange={(e) => setShipment(prev => ({ ...prev, charge_price: parseFloat(e.target.value) }))}
                    sx={{ mt: 2 }}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>
                    }}
                  />
                )}
              </Paper>
            </Grid>

            {/* Botões */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCalculatePrice}
                  disabled={calculatingPrice || !shipment.service || !shipment.sender_zip_code || !shipment.recipient_zip_code || Object.keys(volumeErrors).length > 0}
                  sx={{ 
                    bgcolor: darkBlueColor, 
                    '&:hover': { bgcolor: darkBlueColor },
                    '& .MuiSvgIcon-root': { color: 'white' },
                    color: 'white'
                  }}
                >
                  {calculatingPrice ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    <>
                      <CalculateIcon sx={{ mr: 1 }} />
                      Calcular Preço
                    </>
                  )}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || Object.keys(volumeErrors).length > 0}
                  sx={{ bgcolor: darkBlueColor, '&:hover': { bgcolor: darkBlueColor }, color: 'white' }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    'Criar Envio'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {Object.keys(volumeErrors).length > 0 && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {Object.keys(volumeErrors).map((key, index) => (
              <Typography key={index} variant="body2">Volume {parseInt(key) + 1}:</Typography>
            ))}
            {Object.values(volumeErrors).flat().map((error, index) => (
              <Typography key={index} variant="body2">{error}</Typography>
            ))}
          </Alert>
        )}

        <Dialog
          open={openPriceDialog}
          onClose={handleClosePriceDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ p: 3, color: darkBlueColor, fontWeight: 600 }}>
            Preço Estimado
          </DialogTitle>
          <DialogContent>
            {calculatingPrice ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : priceResult ? (
              <Box>
                {/* Aviso de preço estimado */}
                <Typography variant="body2" sx={{ mb: 2, color: '#ff9800', fontSize: '1rem' }}>
                  ⚠️ Estes são preços estimados. Podem acrescer outras taxas até que a encomenda passe na passadeira da transportadora.
                </Typography>

                {/* Pesos */}
                <Typography variant="body1" gutterBottom>
                  Peso Volumétrico: {priceResult.volumetric_weight}Kg
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Peso Real: {volumes.reduce((sum, vol) => sum + (parseFloat(vol.weight) || 0), 0).toFixed(2)}Kg
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Preços */}
                <Typography variant="body1" gutterBottom>
                  Preço do envio: {parseFloat(priceResult.shipment_price).toFixed(2)} {priceResult.currency}
                </Typography>
                {parseFloat(priceResult.fuel_tax) > 0 && (
                  <Typography variant="body1" gutterBottom>
                    Taxa de Combustível: {parseFloat(priceResult.fuel_tax).toFixed(2)} {priceResult.currency}
                  </Typography>
                )}
                {parseFloat(priceResult.fees_price) > 0 && (
                  <Typography variant="body1" gutterBottom>
                    Taxas Adicionais: {parseFloat(priceResult.fees_price).toFixed(2)} {priceResult.currency}
                  </Typography>
                )}
                <Typography variant="body1" gutterBottom>
                  Subtotal: {parseFloat(priceResult.subtotal).toFixed(2)} {priceResult.currency}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  IVA (23%): {parseFloat(priceResult.vat).toFixed(2)} {priceResult.currency}
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Total e Mensagens */}
                <Typography variant="h6" gutterBottom>
                  Total: {parseFloat(priceResult.total).toFixed(2)} {priceResult.currency}
                </Typography>

                {/* Mensagens de Erro */}
                {priceResult.error && (
                  <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    ❌ Erro: {priceResult.error}
                  </Typography>
                )}
                {priceResult.message && priceResult.message !== "Price calculated successfully." && (
                  <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
                    ℹ️ {priceResult.message}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography>Sem dados de preço</Typography>
            )}
          </DialogContent>
          <DialogContent sx={{ pt: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  onClick={handleClosePriceDialog}
                  fullWidth
                  sx={{ 
                    bgcolor: darkBlueColor, 
                    '&:hover': { bgcolor: darkBlueColor }, 
                    color: 'white',
                    py: 1.5,
                    borderRadius: 1
                  }}
                >
                  Fechar
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>

        {/* Diálogo de Confirmação */}
        <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
          <DialogTitle>Confirmar Pagamento</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Aceita fazer o pagamento do envio com um valor de {shipmentPrice.toFixed(2)}€?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Saldo atual: {balance.toFixed(2)}€
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={() => setOpenConfirmDialog(false)}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmShipment}
                disabled={loading || balance < shipmentPrice || Object.keys(volumeErrors).length > 0}
              >
                {loading ? <CircularProgress size={24} /> : 'Confirmar'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Modal de Erro */}
        <Dialog open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
          <DialogTitle sx={{ color: 'error.main' }}>Erro</DialogTitle>
          <DialogContent>
            <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
              {errorMessage}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => {
                  setErrorModalOpen(false);
                  setOpenConfirmDialog(false); // Fecha também o diálogo de confirmação
                }}
              >
                Corrigir
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Modal de Sucesso */}
        <Dialog
          open={successModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ p: 3, color: darkBlueColor, fontWeight: 600 }}>
            Envio Criado com Sucesso!
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              O seu envio foi criado com sucesso.
            </Typography>
            <Typography variant="body1" color="primary" sx={{ mb: 3 }}>
              Número de Tracking: {createdTracking}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  onClick={handlePrintLabel}
                  startIcon={<LocalPrintshopIcon />}
                  fullWidth
                  sx={{ 
                    bgcolor: darkBlueColor, 
                    '&:hover': { bgcolor: darkBlueColor }, 
                    color: 'white',
                    py: 1.5,
                    borderRadius: 1
                  }}
                >
                  Imprimir Etiqueta
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  onClick={handlePrintGuide}
                  startIcon={<ReceiptIcon />}
                  fullWidth
                  sx={{ 
                    bgcolor: darkBlueColor, 
                    '&:hover': { bgcolor: darkBlueColor }, 
                    color: 'white',
                    py: 1.5,
                    borderRadius: 1
                  }}
                >
                  Imprimir Guia
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  onClick={handleCreateNewShipment}
                  startIcon={<AddIcon />}
                  fullWidth
                  sx={{ 
                    bgcolor: darkBlueColor, 
                    '&:hover': { bgcolor: darkBlueColor }, 
                    color: 'white',
                    py: 1.5,
                    borderRadius: 1
                  }}
                >
                  Criar Novo Envio
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  onClick={handleViewShipments}
                  startIcon={<ListAltIcon />}
                  fullWidth
                  sx={{ 
                    bgcolor: darkBlueColor, 
                    '&:hover': { bgcolor: darkBlueColor }, 
                    color: 'white',
                    py: 1.5,
                    borderRadius: 1
                  }}
                >
                  Ver Meus Envios
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
